import { doc, getDoc, addDoc, collection } from 'firebase/firestore';
import { db } from './config';

/**
 * The core function that sends an email by creating a document in the 'mail' collection.
 */
// ✅ MODIFIED: Added appId to the function signature and the document data
export const sendNotificationEmail = async ({ to, subject, html, appId }) => {
   // --- FIX: This validation is now more robust. ---
    // It filters out any invalid recipients (null, undefined, empty strings)
    // instead of failing the entire operation.
    if (!to || !Array.isArray(to) || to.length === 0) {
        console.error("Email not sent: 'to' array is missing or empty.", { to });
        return;
    }

    const validRecipients = to.filter(email => typeof email === 'string' && email.includes('@'));

    if (validRecipients.length === 0) {
        console.error("Email not sent: No valid recipient addresses found in the 'to' array.", { originalRecipients: to });
        return;
    }

    await addDoc(collection(db, 'mail'), {
        to: validRecipients, // This now sends to the clean, validated list of emails
        message: { subject, html },
        appId: appId || "admin-c78c6" // Pass the appId, with a fallback
    });
};

/**
 * Helper function to get the correct finance email based on entity.
 */
export const getFinanceEmailByEntity = async (entity) => {
    if (!entity) return null;
    const configDoc = await getDoc(doc(db, 'config', 'notifications'));
    const financeEmails = configDoc.data()?.financeEmails;
    return financeEmails ? financeEmails[entity] : null;
};


// =====================================================================================
// ## EMAIL TEMPLATES
// =====================================================================================

/**
 * 1. Email to approver when a new purchase request is sent to them.
 */
export const newPurchaseRequestTemplate = (request, requesterName) => ({
    subject: `New Purchase Request for Approval: ${request.requestNumber}`,
    html: `
        <p>Hello,</p>
        <p>A new purchase request has been submitted by <strong>${requesterName}</strong> and requires your approval.</p>
        <ul style="list-style-type: none; padding: 0;">
            <li style="margin-bottom: 10px;"><strong>Request ID:</strong> ${request.requestNumber}</li>
            <li style="margin-bottom: 10px;"><strong>Description:</strong> ${request.description}</li>
            <li style="margin-bottom: 10px;"><strong>Vendor:</strong> ${request.vendorType === 'new' ? request.newVendorName : request.selectedVendor}</li>
            <li style="margin-bottom: 10px;"><strong>Value:</strong> ${request.currency} ${request.approxValue}</li>
        </ul>
        <p>Please log in to the admin platform to review it in the Approval Center.</p>
    `
});

/**
 * 2. Email to user when there is a status change on their request.
 */
export const statusChangeTemplate = (item, newStatus) => ({
    subject: `Update on your request: ${item.requestNumber || item.invoiceNumber}`,
    html: `
        <p>Hello,</p>
        <p>The status of your request (<strong>${item.requestNumber || item.invoiceNumber}</strong>) has been updated to: <strong>${newStatus}</strong>.</p>
        ${item.approvalComments ? `<p><strong>Approver's Comments:</strong> ${item.approvalComments}</p>` : ''}
        ${item.financeRejectionReason ? `<p><strong>Finance Comments:</strong> ${item.financeRejectionReason}</p>` : ''}
        <p>You can view the details by logging into the admin platform.</p>
    `
});

/**
 * 3. Email to approver when a new invoice is submitted to them.
 */
export const newInvoiceTemplate = (invoice, submitterName) => ({
    subject: `New Invoice for Approval: ${invoice.invoiceNumber}`,
    html: `
        <p>Hello,</p>
        <p>A new invoice has been submitted by <strong>${submitterName}</strong> and requires your approval.</p>
        <ul style="list-style-type: none; padding: 0;">
            <li style="margin-bottom: 10px;"><strong>Invoice ID:</strong> ${invoice.invoiceNumber}</li>
            <li style="margin-bottom: 10px;"><strong>Related PR:</strong> ${invoice.requestNumber}</li>
            <li style="margin-bottom: 10px;"><strong>Vendor:</strong> ${invoice.vendorName}</li>
            <li style="margin-bottom: 10px;"><strong>Amount:</strong> ${invoice.currency} ${invoice.amount}</li>
        </ul>
        <p>Please log in to the admin platform to review it in the Approval Center.</p>
    `
});

/**
 * 4. Email to finance email ID when a new vendor has to be approved.
 */
export const newVendorTemplate = (vendor) => ({
    subject: `New Vendor for Approval: ${vendor.companyName}`,
    html: `
        <p>Hello Finance Team,</p>
        <p>A new vendor, <strong>${vendor.companyName}</strong>, has been submitted by <strong>${vendor.submittedBy}</strong> and requires approval and onboarding.</p>
        <p>Please log in to the Vendor Management section of the Finance Center to review and onboard them.</p>
    `
});

/**
 * 5. Email to finance email ID when a purchase or invoice is approved.
 */
export const financeApprovalTemplate = (item, itemType) => ({
    subject: `${itemType} Approved and Ready for Finance Review: ${item.requestNumber || item.invoiceNumber}`,
    html: `
        <p>Hello Finance Team,</p>
        <p>The following ${itemType.toLowerCase()} has been approved and is now ready for your review:</p>
        <ul style="list-style-type: none; padding: 0;">
            <li style="margin-bottom: 10px;"><strong>ID:</strong> ${item.requestNumber || item.invoiceNumber}</li>
            <li style="margin-bottom: 10px;"><strong>Vendor:</strong> ${item.vendorName || (item.vendorType === 'new' ? item.newVendorName : item.selectedVendor)}</li>
            <li style="margin-bottom: 10px;"><strong>Amount:</strong> ${item.currency} ${item.approxValue || item.amount}</li>
            <li style="margin-bottom: 10px;"><strong>Billing Entity:</strong> ${item.billingEntity}</li>
        </ul>
        <p>Please log in to the Payment Center to process the payment.</p>
    `
});

/**
 * 6. Email to user and approver if finance rejects an invoice.
 */
export const financeRejectionTemplate = (invoice) => ({
    subject: `Action Required: Invoice Rejected by Finance - ${invoice.invoiceNumber}`,
    html: `
        <p>Hello,</p>
        <p>Invoice <strong>${invoice.invoiceNumber}</strong> for vendor <strong>${invoice.vendorName}</strong> has been rejected by the Finance team.</p>
        <p><strong>Reason for Rejection:</strong> ${invoice.financeRejectionReason}</p>
        <p>The original submitter can now edit and resubmit the invoice from the "My Requests" section of the Procurement Center.</p>
    `
});

/**
 * Email to user when their requested vendor is approved.
 */
export const vendorApprovedTemplate = (vendorName) => ({
    subject: `Vendor Approved: ${vendorName}`,
    html: `
        <p>Hello,</p>
        <p>Your request to add the vendor <strong>${vendorName}</strong> has been approved and they are now active in the system.</p>
    `
});

/**
 * Email to user when their requested vendor is rejected.
 */
export const vendorRejectedTemplate = (vendorName) => ({
    subject: `Update on New Vendor Request: ${vendorName}`,
    html: `
        <p>Hello,</p>
        <p>Your request to add the vendor <strong>${vendorName}</strong> has been rejected by the finance team.</p>
        <p>Please contact finance for more details.</p>
    `
});

// --- ADD THESE THREE TEMPLATES TO notifications.js ---

/**
 * Email to admins when a new user registers and is pending approval.
 */
export const newUserRegistrationTemplate = (newUser) => ({
    subject: `New User Registration Pending Approval: ${newUser.name}`,
    html: `
        <p>Hello Admin Team,</p>
        <p>A new user has registered on the portal and is awaiting approval.</p>
        <ul style="list-style-type: none; padding: 0;">
            <li style="margin-bottom: 10px;"><strong>Name:</strong> ${newUser.name}</li>
            <li style="margin-bottom: 10px;"><strong>Email:</strong> ${newUser.email}</li>
        </ul>
        <p>Please log in to the Admin Center to review and approve their account.</p>
    `
});

/**
 * Email to the user when their account is approved by an admin.
 */
export const userApprovedTemplate = (approvedUser) => ({
    subject: `Your Account Has Been Approved`,
    html: `
        <p>Hello ${approvedUser.name},</p>
        <p>Welcome! Your account on the portal has been approved. You can now log in with your Google account.</p>
        <p>Your initial account details are:</p>
        <ul style="list-style-type: none; padding: 0;">
            <li style="margin-bottom: 10px;"><strong>Department:</strong> ${approvedUser.department}</li>
            <li style="margin-bottom: 10px;"><strong>Role:</strong> ${approvedUser.role}</li>
        </ul>
        <p>An administrator may update these details later if needed.</p>
    `
});

/**
 * Email to the user when their department or role is changed by an admin.
 */
export const userDetailsUpdatedTemplate = (updatedUser) => ({
    subject: `Your Account Details Have Been Updated`,
    html: `
        <p>Hello ${updatedUser.name},</p>
        <p>An administrator has updated your account details on the portal.</p>
        <p>Your new details are:</p>
        <ul style="list-style-type: none; padding: 0;">
            <li style="margin-bottom: 10px;"><strong>Department:</strong> ${updatedUser.department}</li>
            <li style="margin-bottom: 10px;"><strong>Role:</strong> ${updatedUser.role}</li>
        </ul>
        <p>If you have any questions, please contact an administrator.</p>
    `
});

/**
 * Email to a manager when a new leave request is submitted for their approval.
 */
export const newLeaveRequestTemplate = (request, requester, violations = []) => {
    let violationHtml = '';
    if (violations.length > 0) {
        violationHtml = `
            <p style="background-color: #FEF3C7; border-left: 4px solid #FBBF24; padding: 12px; margin-top: 16px;">
                <strong>Policy Compliance Note:</strong> This request was submitted despite the following policy violations:
                <ul style="margin-top: 8px;">
                    ${violations.map(v => `<li>${v}</li>`).join('')}
                </ul>
            </p>
        `;
    }

    return {
        subject: `New Leave Request from ${requester.name}: ${request.leaveType}`,
        html: `
            <p>Hello,</p>
            <p>A new leave request has been submitted by <strong>${requester.name}</strong> and requires your approval.</p>
            <ul style="list-style-type: none; padding: 0;">
                <li style="margin-bottom: 10px;"><strong>Requester:</strong> ${requester.name}</li>
                <li style="margin-bottom: 10px;"><strong>Leave Type:</strong> ${request.leaveType}</li>
                <li style="margin-bottom: 10px;"><strong>Start Date:</strong> ${request.startDate.toDate().toLocaleDateString()}</li>
                <li style="margin-bottom: 10px;"><strong>End Date:</strong> ${request.endDate.toDate().toLocaleDateString()}</li>
                <li style="margin-bottom: 10px;"><strong>Total Days:</strong> ${request.duration}</li>
                <li style="margin-bottom: 10px;"><strong>Reason:</strong> ${request.reason}</li>
            </ul>
            ${violationHtml}
            <p>Please log in to the Leave Approval Center to review this request.</p>
        `
    };
};

/**
 * Email to a manager when an employee cancels a leave request.
 */
export const leaveCancellationTemplate = (request, requester, reason, isPostDated) => {
    const postDatedWarning = isPostDated ? 
        `<p style="background-color: #FEF9C3; border-left: 4px solid #FCD34D; padding: 12px; margin-top: 16px;">
            <strong>Important:</strong> This leave request was cancelled after its start date had already passed.
         </p>` : '';

    return {
        subject: `Leave Cancellation Notification from ${requester.name}`,
        html: `
            <p>Hello,</p>
            <p>This is a notification that <strong>${requester.name}</strong> has requested to cancel a leave.</p>
            <ul style="list-style-type: none; padding: 0;">
                <li style="margin-bottom: 10px;"><strong>Leave Type:</strong> ${request.leaveType}</li>
                <li style="margin-bottom: 10px;"><strong>Original Dates:</strong> ${request.startDate.toDate().toLocaleDateString()} to ${request.endDate.toDate().toLocaleDateString()}</li>
                <li style="margin-bottom: 10px;"><strong>Reason for Cancellation:</strong> ${reason}</li>
            </ul>
            ${postDatedWarning}
            <p>The status of this request has been updated. Please review it in the Leave Approval Center.</p>
        `
    };
};

/**
 * Email to an employee when their leave request status is updated by a manager.
 */
export const leaveStatusUpdateTemplate = (request, newStatus, comments = "") => {
    const commentsHtml = comments ? `<p><strong>Manager's Comments:</strong> ${comments}</p>` : '';

    return {
        subject: `Update on Your Leave Request: ${newStatus}`,
        html: `
            <p>Hello,</p>
            <p>The status of your ${request.leaveType} request for ${request.startDate.toDate().toLocaleDateString()} to ${request.endDate.toDate().toLocaleDateString()} has been updated to: <strong>${newStatus}</strong>.</p>
            ${commentsHtml}
            <p>You can view the details in the "My Leave Requests" section of the portal.</p>
        `
    };
};


/**
 * Email to a manager when a new expense report is submitted for their approval.
 */
export const newExpenseReportTemplate = (report, requesterName) => ({
    subject: `New Expense Report for Approval: ${report.reportName}`,
    html: `
        <p>Hello,</p>
        <p>A new expense report has been submitted by <strong>${requesterName}</strong> and requires your approval.</p>
        <ul style="list-style-type: none; padding: 0;">
            <li style="margin-bottom: 10px;"><strong>Report Name:</strong> ${report.reportName}</li>
            <li style="margin-bottom: 10px;"><strong>Total Amount:</strong> ${report.totalAmount.toFixed(2)}</li>
            <li style="margin-bottom: 10px;"><strong>Entity:</strong> ${report.entity}</li>
        </ul>
        <p>Please log in to the admin platform to review it in the Expense Approval Center.</p>
    `
});

/**
 * Email to Finance when an expense report is approved by a manager.
 */
export const expenseApprovedForFinanceTemplate = (report, managerName) => ({
    subject: `Expense Report Approved & Ready for Reimbursement: ${report.reportNumber}`,
    html: `
        <p>Hello Finance Team,</p>
        <p>The following expense report, submitted by <strong>${report.requesterName}</strong>, has been approved by <strong>${managerName}</strong> and is now ready for reimbursement.</p>
        <ul style="list-style-type: none; padding: 0;">
            <li style="margin-bottom: 10px;"><strong>Report ID:</strong> ${report.reportNumber}</li>
            <li style="margin-bottom: 10px;"><strong>Report Name:</strong> ${report.reportName}</li>
            <li style="margin-bottom: 10px;"><strong>Total Amount:</strong> ${report.totalAmount.toFixed(2)}</li>
            <li style="margin-bottom: 10px;"><strong>Billing Entity:</strong> ${report.entity}</li>
        </ul>
        <p>Please log in to the Finance Center to process the reimbursement.</p>
    `
});

/**
 * Email to an employee when their expense report status is updated.
 */
export const expenseStatusUpdateTemplate = (report, newStatus, comments = "") => {
    const commentsHtml = comments ? `<p><strong>Manager's Comments:</strong> ${comments}</p>` : '';

    return {
        subject: `Update on Your Expense Report: ${report.reportName}`,
        html: `
            <p>Hello,</p>
            <p>The status of your expense report "<strong>${report.reportName}</strong>" has been updated to: <strong>${newStatus}</strong>.</p>
            ${commentsHtml}
            <p>You can view the details in the "My Expense Reports" section of the portal.</p>
        `
    };
};